<template>
  <div
    class="layout-watermark-root"
    :class="{ 'layout-aside-collapsed': isMenuCollapsed }"
    ref="watermarkParent"
  >
    <el-container class="layout-container">
    <!-- Vue2：Element 组件上监听原生鼠标事件必须用 .native，否则不会触发 -->
    <el-aside
      :width="isMenuCollapsed ? '64px' : '200px'"
      class="layout-aside"
      @mouseenter.native="onAsideMouseEnter"
      @mouseleave.native="onAsideMouseLeave"
    >
      <div class="layout-menu-wrapper">
        <el-menu
          v-if="menuReady"
          :default-active="activeMenu"
          :default-openeds="canRoiGroup() ? ['roi'] : []"
          class="layout-menu"
          background-color="#304156"
          text-color="#bfcbd9"
          active-text-color="#409EFF"
          :collapse="isMenuCollapsed"
          :collapse-transition="false"
          router
        >
        <el-menu-item v-if="canMenu('dashboard')" index="/dashboard">
          <i class="el-icon-s-home"></i>
          <span slot="title">首页</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('electronic-map')" index="/electronic-map">
          <i class="el-icon-location-outline"></i>
          <span slot="title">电子地图</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('statistics')" index="/statistics">
          <i class="el-icon-s-data"></i>
          <span slot="title">数据统计</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('org')" index="/org">
          <i class="el-icon-office-building"></i>
          <span slot="title">机构管理</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('staff')" index="/staff">
          <i class="el-icon-user"></i>
          <span slot="title">会员管理</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('student')" index="/student">
          <i class="el-icon-s-custom"></i>
          <span slot="title">学生管理</span>
        </el-menu-item>
        <el-submenu v-if="canRoiGroup()" index="roi">
          <template slot="title">
            <i class="el-icon-s-marketing"></i>
            <span>ROI管理</span>
          </template>
          <el-menu-item v-if="canMenu('roi:promotion')" index="/roi">
            <span slot="title">推广账户管理</span>
          </el-menu-item>
          <el-menu-item v-if="canMenu('roi:overview')" index="/roi-overview">
            <span slot="title">推广账户概况</span>
          </el-menu-item>
          <el-menu-item v-if="canMenu('roi:audit')" index="/roi-audit">
            <span slot="title">ROI监控</span>
          </el-menu-item>
        </el-submenu>
        <el-menu-item v-if="canMenu('baidu-aifanfan')" index="/baidu-aifanfan">
          <i class="el-icon-chat-dot-round"></i>
          <span slot="title">百度爱番番</span>
        </el-menu-item>
        <el-menu-item v-if="canMenu('system-settings')" index="/system-settings">
          <i class="el-icon-setting"></i>
          <span slot="title">系统设置</span>
        </el-menu-item>
        </el-menu>
      </div>
    </el-aside>
    <el-container>
      <el-header class="layout-header">
        <div class="header-brand">
          <img v-if="platformLogo" :src="platformLogo" alt="logo" class="header-brand-logo" />
          <span class="header-brand-title">{{ systemTitle }}</span>
        </div>
        <div class="header-right">
          <el-tooltip content="打开计算器" placement="bottom">
            <el-button type="text" class="header-tool-btn" @click="openWorkbenchTool('calculator')">
              <img :src="calculatorEntryIcon" alt="计算器" class="header-tool-icon" />
            </el-button>
          </el-tooltip>
          <el-tooltip content="打开日历" placement="bottom">
            <el-button type="text" class="header-tool-btn" @click="openWorkbenchTool('calendar')">
              <img :src="calendarEntryIcon" alt="日历" class="header-tool-icon" />
            </el-button>
          </el-tooltip>
          <el-tooltip :content="isFullscreen ? '退出全屏' : '全屏显示'" placement="bottom">
            <el-button 
              type="text" 
              class="fullscreen-btn"
              @click="toggleFullscreen"
            >
              <i :class="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip content="下载消息精灵" placement="bottom">
            <el-button
              type="text"
              class="header-tool-btn header-download-btn"
              @click="downloadNotificationCenter"
            >
              <i class="el-icon-download"></i>
            </el-button>
          </el-tooltip>
          <span class="user-name">
            <i class="el-icon-user-solid"></i>
            {{ account }}
          </span>
          <el-button type="text" class="logout-btn" @click="handleLogout">
            <i class="el-icon-switch-button"></i>
            退出
          </el-button>
        </div>
      </el-header>
      <el-main class="layout-main">
        <router-view />
      </el-main>
      <el-dialog
        :title="activeWorkbenchTool === 'calculator' ? '计算器（基础四则运算）' : '日期日历（月视图）'"
        :visible.sync="workbenchToolDialogVisible"
        width="760px"
        append-to-body
        class="workbench-tool-dialog"
      >
        <div
          v-if="activeWorkbenchTool === 'calculator'"
          class="dialog-calculator"
          :class="{
            'dialog-calculator--business-dark': calculatorTheme === 'business-dark',
            'dialog-calculator--ios-apple': calculatorTheme === 'ios-apple',
            'dialog-calculator--macaron': calculatorTheme === 'macaron',
            'dialog-calculator--cyberpunk': calculatorTheme === 'cyberpunk'
          }"
        >
          <div class="dialog-calculator__theme-switch">
            <button
              type="button"
              class="dialog-theme-btn"
              :class="{ 'dialog-theme-btn--active': calculatorTheme === 'business-dark' }"
              @click="setCalculatorTheme('business-dark')"
            >
              深色极简
            </button>
            <button
              type="button"
              class="dialog-theme-btn"
              :class="{ 'dialog-theme-btn--active': calculatorTheme === 'ios-apple' }"
              @click="setCalculatorTheme('ios-apple')"
            >
              iOS 风
            </button>
            <button
              type="button"
              class="dialog-theme-btn"
              :class="{ 'dialog-theme-btn--active': calculatorTheme === 'macaron' }"
              @click="setCalculatorTheme('macaron')"
            >
              马卡龙
            </button>
            <button
              type="button"
              class="dialog-theme-btn"
              :class="{ 'dialog-theme-btn--active': calculatorTheme === 'cyberpunk' }"
              @click="setCalculatorTheme('cyberpunk')"
            >
              赛博朋克
            </button>
          </div>
          <div class="dialog-calculator__display">
            <div class="dialog-calculator__history">
              上次：{{ workbenchCalculator.lastExpression || '-' }}
            </div>
            <div class="dialog-calculator__expression">
              {{ workbenchCalculator.expression }}
            </div>
            <div class="dialog-calculator__result">{{ workbenchCalculator.result }}</div>
          </div>

          <div class="dialog-calculator__keypad">
            <button class="keypad-btn keypad-btn--danger" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('ac') }" type="button" @click="clearCalculatorExpression">AC</button>
            <button class="keypad-btn keypad-btn--soft" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('paren-left') }" type="button" @click="pressCalculatorToken('paren-left', '(')">(</button>
            <button class="keypad-btn keypad-btn--soft" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('paren-right') }" type="button" @click="pressCalculatorToken('paren-right', ')')">)</button>
            <button class="keypad-btn keypad-btn--soft" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('backspace') }" type="button" @click="pressCalculatorBackspace">⌫</button>

            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-7') }" type="button" @click="pressCalculatorToken('digit-7', '7')">7</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-8') }" type="button" @click="pressCalculatorToken('digit-8', '8')">8</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-9') }" type="button" @click="pressCalculatorToken('digit-9', '9')">9</button>
            <button class="keypad-btn keypad-btn--operator" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('op-div') }" type="button" @click="pressCalculatorOperator('op-div', '/')">÷</button>

            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-4') }" type="button" @click="pressCalculatorToken('digit-4', '4')">4</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-5') }" type="button" @click="pressCalculatorToken('digit-5', '5')">5</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-6') }" type="button" @click="pressCalculatorToken('digit-6', '6')">6</button>
            <button class="keypad-btn keypad-btn--operator" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('op-mul') }" type="button" @click="pressCalculatorOperator('op-mul', '*')">×</button>

            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-1') }" type="button" @click="pressCalculatorToken('digit-1', '1')">1</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-2') }" type="button" @click="pressCalculatorToken('digit-2', '2')">2</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-3') }" type="button" @click="pressCalculatorToken('digit-3', '3')">3</button>
            <button class="keypad-btn keypad-btn--operator" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('op-sub') }" type="button" @click="pressCalculatorOperator('op-sub', '-')">-</button>

            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('dot') }" type="button" @click="pressCalculatorToken('dot', '.')">.</button>
            <button class="keypad-btn" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('digit-0') }" type="button" @click="pressCalculatorToken('digit-0', '0')">0</button>
            <button class="keypad-btn keypad-btn--equal" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('equal') }" type="button" @click="pressCalculatorEqual">=</button>
            <button class="keypad-btn keypad-btn--operator" :class="{ 'keypad-btn--latched': isCalculatorKeyActive('op-add') }" type="button" @click="pressCalculatorOperator('op-add', '+')">+</button>
          </div>
        </div>

        <el-calendar v-else v-model="workbenchCalendarDate" />
      </el-dialog>
    </el-container>
    </el-container>
    <baidu-lead-notifier />
  </div>
</template>

<script>
import { getSystemConfig, DEFAULT_LOGO_URL } from '@/utils/systemConfig';
import { getAccount, clearSessionAuth, getAllowedMenus } from '@/utils/sessionAuth';
import { hasRoiGroupAccess, isMenuKeyAllowed } from '@/utils/menuPermissions';
import api from '@/services/api';
import { Watermark } from 'watermark-js-plus';
import BaiduLeadNotifier from '@/components/BaiduLeadNotifier.vue';
import calculatorEntryIcon from '@/static/iconImage/calcutor.png';
import calendarEntryIcon from '@/static/iconImage/daily.png';
import {
  getNotificationCenterDownloadUrl
} from '@/utils/notificationCenterDownload.mjs';

const CALCULATOR_CACHE_KEY = 'workbenchCalculatorSnapshot';
const CALCULATOR_THEME_OPTIONS = ['business-dark', 'ios-apple', 'macaron', 'cyberpunk'];

export default {
  name: 'Layout',
  components: {
    BaiduLeadNotifier
  },
  data() {
    return {
      isFullscreen: false,
      menuReady: false,
      isMenuCollapsed: true,
      asideLeaveTimer: null,
      systemTitle: '学途无忧后台管理系统',
      systemTitleShort: '学途无忧网',
      platformLogo: DEFAULT_LOGO_URL,
      calculatorEntryIcon,
      calendarEntryIcon,
      workbenchToolDialogVisible: false,
      activeWorkbenchTool: 'calculator',
      calculatorTheme: 'business-dark',
      workbenchCalendarDate: new Date(),
      workbenchCalculator: {
        lastExpression: '',
        expression: '0',
        result: '0'
      },
      activeCalculatorKey: '',

      watermarkInstance: null,
      watermarkConfig: null,
      watermarkIp: ''
    };
  },
  computed: {
    activeMenu() {
      return this.$route.path;
    },
    account() {
      return getAccount() || '管理员';
    }
  },
  mounted() {
    this.loadSystemConfig();
    this.loadCalculatorSnapshot();
    this.$root.$on('system-config-updated', this.loadSystemConfig);
    this.$root.$on('watermark-config-updated', this.reloadWatermarkConfig);
    this.$nextTick(() => {
      this.menuReady = true;
    });
    this.reloadWatermarkConfig();
    this.enterFullscreen();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    // 折叠时子菜单弹出层在 body 上，从侧栏移入弹出层会触发 aside 的 mouseleave，需取消延迟折叠
    document.addEventListener('mouseover', this.onDocMouseOverMenuPopup, true);
    document.addEventListener('keydown', this.handleCalculatorKeydown);
  },
  beforeDestroy() {
    this.$root.$off('system-config-updated', this.loadSystemConfig);
    this.$root.$off('watermark-config-updated', this.reloadWatermarkConfig);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mouseover', this.onDocMouseOverMenuPopup, true);
    document.removeEventListener('keydown', this.handleCalculatorKeydown);
    this.clearAsideLeaveTimer();
    this.destroyWatermark();
  },
  methods: {
    loadCalculatorSnapshot() {
      try {
        const raw = localStorage.getItem(CALCULATOR_CACHE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const expression = typeof parsed.expression === 'string' && parsed.expression.trim()
          ? parsed.expression.trim()
          : '0';
        const lastExpression = typeof parsed.lastExpression === 'string'
          ? parsed.lastExpression.trim()
          : '';
        const result = typeof parsed.result === 'string' && parsed.result.trim()
          ? parsed.result.trim()
          : '0';
        const theme = typeof parsed.theme === 'string' && CALCULATOR_THEME_OPTIONS.includes(parsed.theme)
          ? parsed.theme
          : 'business-dark';
        this.calculatorTheme = theme;
        this.workbenchCalculator.lastExpression = lastExpression;
        this.workbenchCalculator.expression = expression;
        this.workbenchCalculator.result = result;
      } catch (e) {
        this.calculatorTheme = 'business-dark';
        this.workbenchCalculator.lastExpression = '';
        this.workbenchCalculator.expression = '0';
        this.workbenchCalculator.result = '0';
      }
    },
    persistCalculatorSnapshot() {
      try {
        const payload = {
          theme: this.calculatorTheme,
          lastExpression: String(this.workbenchCalculator.lastExpression || ''),
          expression: String(this.workbenchCalculator.expression || '0'),
          result: String(this.workbenchCalculator.result || '0')
        };
        localStorage.setItem(CALCULATOR_CACHE_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore
      }
    },
    setCalculatorTheme(theme) {
      if (!CALCULATOR_THEME_OPTIONS.includes(theme)) return;
      this.calculatorTheme = theme;
      this.persistCalculatorSnapshot();
    },
    loadSystemConfig() {
      const c = getSystemConfig();
      this.systemTitle = c.systemTitle;
      this.systemTitleShort = c.systemTitleShort;
      this.platformLogo = c.platformLogo;
    },

    destroyWatermark() {
      try {
        if (this.watermarkInstance && typeof this.watermarkInstance.destroy === 'function') {
          this.watermarkInstance.destroy();
        }
      } catch (e) {
        // ignore
      }
      this.watermarkInstance = null;
    },

    formatDateYYYYMMDD(d) {
      const dt = d instanceof Date ? d : new Date();
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    },

    fillWatermarkTemplate(tpl, vars) {
      const text = typeof tpl === 'string' ? tpl : '';
      const safe = text || '';
      const replaceVar = (key, value) => {
        const v = value == null ? '' : String(value);
        return safe
          .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), v)
          .replace(new RegExp(`\\{\\s*${key}\\s*\\}`, 'g'), v);
      };

      let out = safe;
      Object.keys(vars || {}).forEach((k) => {
        const v = vars[k];
        out = out
          .replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v == null ? '' : String(v))
          .replace(new RegExp(`\\{\\s*${k}\\s*\\}`, 'g'), v == null ? '' : String(v));
      });
      return out;
    },

    async reloadWatermarkConfig() {
      try {
        const [cfgRes, ipRes] = await Promise.all([
          api.post('/system/watermark-config/query', {}),
          this.watermarkIp ? Promise.resolve({ data: { success: true, data: { ip: this.watermarkIp } } }) : api.post('/system/client-ip', {})
        ]);
        if (cfgRes.data && cfgRes.data.success) {
          this.watermarkConfig = cfgRes.data.data || null;
        }
        if (ipRes.data && ipRes.data.success && ipRes.data.data && ipRes.data.data.ip) {
          this.watermarkIp = ipRes.data.data.ip;
        }
      } catch (e) {
        // ignore
      }

      this.applyWatermark();
    },

    applyWatermark() {
      this.destroyWatermark();
      const cfg = this.watermarkConfig;
      if (!cfg || !cfg.enabled) return;
      if (!this.$refs.watermarkParent) return;

      const username = this.account || '';
      const ip = this.watermarkIp || '';
      const date = this.formatDateYYYYMMDD(new Date());
      const content = this.fillWatermarkTemplate(cfg.contentTemplate, { username, ip, date });

      const options = {
        parent: this.$refs.watermarkParent,
        content,
        width: cfg.width,
        height: cfg.height,
        rotate: cfg.rotate,
        layout: cfg.layoutType || 'grid',
        gridLayoutOptions:
          (cfg.layoutType || 'grid') === 'grid'
            ? {
                rows: cfg.gridRows,
                cols: cfg.gridCols,
                gap: [cfg.gapX, cfg.gapY]
              }
            : undefined,
        translateX: cfg.translateX,
        translateY: cfg.translateY,
        fontSize: `${cfg.fontSize}px`,
        fontColor: cfg.fontColor,
        globalAlpha: cfg.globalAlpha,
        zIndex: 1,
        onSuccess: () => {
          // 避免水印遮挡点击
          try {
            const canvases = this.$refs.watermarkParent ? this.$refs.watermarkParent.querySelectorAll('canvas') : [];
            canvases.forEach((c) => {
              c.style.pointerEvents = 'none';
            });
          } catch (e) {
            // ignore
          }
        }
      };

      try {
        this.watermarkInstance = new Watermark(options);
        this.watermarkInstance.create();
      } catch (e) {
        // ignore
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    },
    enterFullscreen() {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    },
    downloadNotificationCenter() {
      const targetUrl = getNotificationCenterDownloadUrl(window.location.origin);
      window.location.assign(targetUrl);
    },
    openWorkbenchTool(tool) {
      this.activeWorkbenchTool = tool === 'calendar' ? 'calendar' : 'calculator';
      this.workbenchToolDialogVisible = true;
    },
    isCalculatorKeyActive(keyId) {
      return this.activeCalculatorKey === keyId;
    },
    setActiveCalculatorKey(keyId) {
      this.activeCalculatorKey = keyId || '';
    },
    pressCalculatorToken(keyId, token) {
      this.setActiveCalculatorKey(keyId);
      this.appendCalculatorToken(token);
    },
    pressCalculatorOperator(keyId, operator) {
      this.setActiveCalculatorKey(keyId);
      this.setWorkbenchCalculatorOperator(operator);
    },
    pressCalculatorBackspace() {
      this.setActiveCalculatorKey('backspace');
      this.backspaceCalculatorExpression();
    },
    pressCalculatorEqual() {
      this.setActiveCalculatorKey('equal');
      this.calculateWorkbenchResult();
    },
    setWorkbenchCalculatorOperator(operator) {
      this.appendCalculatorToken(operator);
    },
    getNormalizedCalculatorExpression() {
      return String(this.workbenchCalculator.expression || '')
        .replace(/\s+/g, '')
        .replace(/×/g, '*')
        .replace(/÷/g, '/');
    },
    getCalculatorParenBalance(expression) {
      let balance = 0;
      for (let i = 0; i < expression.length; i += 1) {
        if (expression[i] === '(') balance += 1;
        if (expression[i] === ')') balance -= 1;
        if (balance < 0) return -1;
      }
      return balance;
    },
    isCalculatorOperatorChar(ch) {
      return /^[+\-*/]$/.test(ch || '');
    },
    isCalculatorDigitChar(ch) {
      return /^\d$/.test(ch || '');
    },
    appendCalculatorToken(token) {
      const raw = String(this.workbenchCalculator.expression || '');
      const current = raw === '0' ? '' : raw;
      const last = current.slice(-1);

      if (token === '.') {
        // 小数点左侧只能是数字，或由运算符/左括号开头（自动补 0）
        if (last === ')' || last === '.') return;
        const segment = current.split(/[+\-*/()]/).pop() || '';
        if (segment.includes('.')) return;
        if (!current || /[+\-*/(]$/.test(current)) {
          this.workbenchCalculator.expression = `${current}0.`;
        } else {
          this.workbenchCalculator.expression = `${current}.`;
        }
        this.persistCalculatorSnapshot();
        return;
      }

      if (this.isCalculatorDigitChar(token)) {
        // 右括号后不能直接跟数字，必须先输入运算符
        if (last === ')') return;
        const next = `${current}${token}`;
        this.workbenchCalculator.expression = next || '0';
        this.persistCalculatorSnapshot();
        return;
      }

      if (token === '(') {
        // 左括号左侧只能是起始位、运算符或左括号；禁止数字/右括号/连续左括号
        if (!current) {
          this.workbenchCalculator.expression = '(';
          this.persistCalculatorSnapshot();
          return;
        }
        if (this.isCalculatorDigitChar(last) || last === ')' || last === '(' || last === '.') return;
        if (!this.isCalculatorOperatorChar(last)) return;
        this.workbenchCalculator.expression = `${current}(`;
        this.persistCalculatorSnapshot();
        return;
      }

      if (token === ')') {
        // 右括号左侧必须是数字或右括号（禁止空括号与运算符后直接闭合）
        const balance = this.getCalculatorParenBalance(current);
        if (balance <= 0) return;
        if (!this.isCalculatorDigitChar(last) && last !== ')') return;
        this.workbenchCalculator.expression = `${current})`;
        this.persistCalculatorSnapshot();
        return;
      }

      if (/^[+\-*/]$/.test(token)) {
        if (!current) {
          // 起始位仅允许负号（单目负号）
          if (token === '-') {
            this.workbenchCalculator.expression = '-';
            this.persistCalculatorSnapshot();
          }
          return;
        }
        // 运算符后若再次输入运算符：仅允许在 + - * / ( 后面追加单目负号
        if (this.isCalculatorOperatorChar(last) || last === '(') {
          if (token === '-' && last !== '-') {
            this.workbenchCalculator.expression = `${current}-`;
            this.persistCalculatorSnapshot();
          }
          return;
        }
        // 小数点后不能直接接运算符（例如 1.+）
        if (last === '.') return;
        this.workbenchCalculator.expression = `${current}${token}`;
        this.persistCalculatorSnapshot();
      }
    },
    handleCalculatorKeydown(event) {
      if (!this.workbenchToolDialogVisible || this.activeWorkbenchTool !== 'calculator') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;
      const code = event.code;
      if (/^\d$/.test(key)) {
        this.setActiveCalculatorKey(`digit-${key}`);
        this.appendCalculatorToken(key);
        event.preventDefault();
        return;
      }

      if (key === '.' || code === 'NumpadDecimal') {
        this.setActiveCalculatorKey('dot');
        this.appendCalculatorToken('.');
        event.preventDefault();
        return;
      }

      const operatorByCode = {
        NumpadAdd: '+',
        NumpadSubtract: '-',
        NumpadMultiply: '*',
        NumpadDivide: '/'
      };
      const operatorByKey = {
        '+': '+',
        '-': '-',
        '*': '*',
        '/': '/',
        '＋': '+',
        '－': '-',
        '×': '*',
        '÷': '/'
      };
      const mappedOperator = operatorByCode[code] || operatorByKey[key];
      if (mappedOperator) {
        const operatorKeyMap = {
          '+': 'op-add',
          '-': 'op-sub',
          '*': 'op-mul',
          '/': 'op-div'
        };
        this.setActiveCalculatorKey(operatorKeyMap[mappedOperator] || '');
        this.setWorkbenchCalculatorOperator(mappedOperator);
        event.preventDefault();
        return;
      }

      if (key === 'Enter' || code === 'NumpadEnter' || (key === '=' && !event.shiftKey)) {
        this.setActiveCalculatorKey('equal');
        this.calculateWorkbenchResult();
        event.preventDefault();
        return;
      }

      if (key === 'Backspace') {
        this.setActiveCalculatorKey('backspace');
        this.backspaceCalculatorExpression();
        event.preventDefault();
        return;
      }

      if (key === 'Delete') {
        this.clearCalculatorExpression();
        event.preventDefault();
        return;
      }

      if (key === 'c' || key === 'C') {
        this.clearCalculatorExpression();
        event.preventDefault();
        return;
      }

      if (key === '(' || (key === '9' && event.shiftKey)) {
        this.setActiveCalculatorKey('paren-left');
        this.appendCalculatorToken('(');
        event.preventDefault();
        return;
      }

      if (key === ')' || (key === '0' && event.shiftKey)) {
        this.setActiveCalculatorKey('paren-right');
        this.appendCalculatorToken(')');
        event.preventDefault();
        return;
      }

      if (key === 'Escape') {
        this.workbenchToolDialogVisible = false;
      }
    },
    clearCalculatorExpression() {
      this.setActiveCalculatorKey('');
      this.workbenchCalculator.expression = '0';
      this.persistCalculatorSnapshot();
    },
    backspaceCalculatorExpression() {
      const current = String(this.workbenchCalculator.expression == null ? '0' : this.workbenchCalculator.expression);
      if (current.length <= 1) {
        this.workbenchCalculator.expression = '0';
        this.persistCalculatorSnapshot();
        return;
      }
      const next = current.slice(0, -1);
      this.workbenchCalculator.expression = next || '0';
      this.persistCalculatorSnapshot();
    },
    calculateWorkbenchResult() {
      let rawExpression = String(this.workbenchCalculator.expression || '').trim();
      if (rawExpression) {
        this.workbenchCalculator.lastExpression = rawExpression;
      }
      let expression = this.getNormalizedCalculatorExpression();
      if (!expression) {
        this.workbenchCalculator.result = '0';
        this.persistCalculatorSnapshot();
        return;
      }
      if (/[^0-9+\-*/().]/.test(expression)) {
        this.workbenchCalculator.result = '表达式非法';
        this.persistCalculatorSnapshot();
        return;
      }
      const balance = this.getCalculatorParenBalance(expression);
      if (balance < 0) {
        this.workbenchCalculator.result = '括号不匹配';
        this.persistCalculatorSnapshot();
        return;
      }
      if (balance > 0) {
        const padding = ')'.repeat(balance);
        rawExpression = `${rawExpression}${padding}`;
        expression = `${expression}${padding}`;
        this.workbenchCalculator.expression = rawExpression;
        this.workbenchCalculator.lastExpression = rawExpression;
      }
      try {
        const result = Function(`"use strict"; return (${expression});`)();
        if (!Number.isFinite(result)) {
          this.workbenchCalculator.result = '计算错误';
          this.persistCalculatorSnapshot();
          return;
        }
        this.workbenchCalculator.result = Number.isInteger(result) ? String(result) : String(Number(result.toFixed(6)));
        this.persistCalculatorSnapshot();
      } catch (e) {
        this.workbenchCalculator.result = '表达式非法';
        this.persistCalculatorSnapshot();
      }
    },
    resetWorkbenchCalculator() {
      this.workbenchCalculator.expression = '0';
      this.workbenchCalculator.result = '0';
      this.persistCalculatorSnapshot();
    },
    clearAsideLeaveTimer() {
      if (this.asideLeaveTimer) {
        clearTimeout(this.asideLeaveTimer);
        this.asideLeaveTimer = null;
      }
    },
    onAsideMouseEnter() {
      this.clearAsideLeaveTimer();
      this.isMenuCollapsed = false;
    },
    onAsideMouseLeave() {
      this.clearAsideLeaveTimer();
      this.asideLeaveTimer = setTimeout(() => {
        this.isMenuCollapsed = true;
        this.asideLeaveTimer = null;
      }, 280);
    },
    onDocMouseOverMenuPopup(e) {
      const t = e.target;
      if (t && typeof t.closest === 'function' && t.closest('.el-menu--popup')) {
        this.clearAsideLeaveTimer();
      }
    },
    canMenu(key) {
      const m = getAllowedMenus();
      if (!m || !m.length) return false;
      return isMenuKeyAllowed(key, m);
    },
    canRoiGroup() {
      const m = getAllowedMenus();
      return hasRoiGroupAccess(m);
    },
    async handleLogout() {
      try {
        await api.post('/auth/logout', {});
      } catch (e) {
        // 退出本地登录态优先，登出日志失败不阻断用户离开。
      }
      clearSessionAuth();
      // 兼容旧版缓存：同时清理 localStorage，避免跨 Tab 干扰
      localStorage.removeItem('token');
      localStorage.removeItem('account');
      localStorage.removeItem('userRole');
      localStorage.removeItem('roleCode');
      localStorage.removeItem('allowedMenus');
      this.$router.push('/login');
      this.$message.success('已退出登录');
    }
  }
};
</script>

<style scoped>
.layout-watermark-root {
  height: 100vh;
  width: 100%;
  position: relative;
}

.layout-container {
  height: 100vh;
}

.layout-aside {
  background-color: #304156;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: width 0.25s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.layout-menu-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.layout-menu {
  border-right: none;
}

.layout-header {
  background-color: #409eff;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  padding: 0 24px;
}

.header-brand {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  max-width: calc(100% - 320px);
}

.header-brand-logo {
  height: 48px;
  width: auto;
  max-width: 56px;
  object-fit: contain;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.25));
}

.header-brand-title {
  font-family: 'Microsoft YaHei', '微软雅黑', 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  font-size: 24px;
  font-weight: 700 !important; /* 覆盖 App.vue 全局 * { font-weight: normal !important } */
  letter-spacing: calc(1px + 3pt); /* 原 1px + 累计增加 3 磅(3pt) */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  /* 上 → 下：#ffffff → #409eff */
  background: linear-gradient(to bottom, #ffffff 0%, #409eff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
  position: relative;
  z-index: 1;
}

.header-tool-btn,
.fullscreen-btn {
  font-size: 18px;
  color: #bfcbd9;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.3s;
}

.header-tool-btn:hover,
.fullscreen-btn:hover {
  color: #79bbff;
  background-color: rgba(64, 158, 255, 0.18);
}

.header-tool-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.user-name {
  color: #e8eef3;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.user-name i {
  color: #66b1ff;
}

.logout-btn {
  color: #a8b4c4;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s;
}

.logout-btn:hover {
  color: #f9a8a8;
  background-color: rgba(245, 108, 108, 0.12);
}

.layout-main {
  background: linear-gradient(180deg, #f5f7fa 0%, #e8ecf0 100%);
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px - 60px);
}

.layout-footer {
  height: 60px;
  background-color: #fff;
  border-top: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #909399;
  gap: 4px;
}

.footer-line {
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-link {
  color: #909399;
  text-decoration: none;
  transition: color 0.3s;
  display: flex;
  align-items: center;
}

.footer-link:hover {
  color: #409EFF;
}

.beian-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 8px;
}

.beian-icon {
  width: 14px;
  height: 14px;
}

.report-link {
  padding-left: 8px;
}

.dialog-calculator {
  border: 1px solid #dbe5ff;
  border-radius: 16px;
  padding: 16px;
  background: linear-gradient(145deg, #f7f9ff 0%, #ffffff 48%, #f4f7ff 100%);
  box-shadow: 0 14px 30px rgba(63, 90, 170, 0.12);
}

.dialog-calculator__theme-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(210, 222, 255, 0.42);
}

.dialog-theme-btn {
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #526088;
  background: transparent;
  cursor: pointer;
  transition: all 0.18s ease;
}

.dialog-theme-btn:hover {
  color: #2f3c66;
  background: rgba(255, 255, 255, 0.66);
}

.dialog-theme-btn--active {
  color: #2147b8;
  background: #fff;
  box-shadow: 0 2px 8px rgba(54, 91, 187, 0.18);
}

.dialog-calculator__display {
  position: relative;
  overflow: hidden;
  background: linear-gradient(155deg, #1d2438 0%, #242d46 45%, #1a2235 100%);
  border-radius: 14px;
  padding: 14px 16px;
  color: #eef3ff;
  border: 1px solid rgba(137, 163, 255, 0.3);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), inset 0 -10px 16px rgba(0, 0, 0, 0.28), 0 8px 18px rgba(24, 32, 56, 0.35);
}

.dialog-calculator__display::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0px,
    rgba(255, 255, 255, 0.05) 2px,
    rgba(255, 255, 255, 0) 6px,
    rgba(255, 255, 255, 0) 8px
  );
  opacity: 0.35;
}

.dialog-calculator__history {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 14px;
  opacity: 0.8;
  min-height: 20px;
  letter-spacing: 0.4px;
  color: #b7c6ff;
}

.dialog-calculator__expression {
  margin-top: 2px;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 16px;
  opacity: 0.95;
  min-height: 24px;
  letter-spacing: 0.5px;
  color: #d8e3ff;
}

.dialog-calculator__result {
  margin-top: 6px;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 38px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: 1px;
  word-break: break-all;
  color: #ffffff;
  text-shadow: 0 0 12px rgba(124, 156, 255, 0.35);
}

.dialog-calculator__keypad {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.keypad-btn {
  border: 1px solid #d8e3ff;
  border-radius: 12px;
  height: 42px;
  background: linear-gradient(180deg, #ffffff 0%, #edf3ff 100%);
  color: #2e3754;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.16s ease;
  box-shadow: 0 3px 0 rgba(71, 101, 184, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.keypad-btn:hover {
  background: linear-gradient(180deg, #ffffff 0%, #e7efff 100%);
  border-color: #c2d4ff;
}

.keypad-btn:active {
  transform: translateY(2px) scale(0.99);
  box-shadow: inset 0 3px 7px rgba(27, 43, 89, 0.28), inset 0 -1px 0 rgba(255, 255, 255, 0.36);
  filter: saturate(1.02);
}

.keypad-btn--latched {
  transform: translateY(2px) scale(0.99);
  box-shadow: inset 0 3px 7px rgba(27, 43, 89, 0.28), inset 0 -1px 0 rgba(255, 255, 255, 0.36);
  filter: saturate(1.02);
}

.keypad-btn--operator {
  background: linear-gradient(180deg, #eef4ff 0%, #dce8ff 100%);
  color: #2855d2;
  border-color: #bfd2ff;
}

.keypad-btn--equal {
  background: linear-gradient(180deg, #5ca8ff 0%, #3c7cff 100%);
  color: #fff;
  border-color: #4d89ff;
  box-shadow: 0 3px 0 rgba(44, 92, 202, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.keypad-btn--equal:hover {
  background: linear-gradient(180deg, #6ab2ff 0%, #4886ff 100%);
}

.keypad-btn--soft {
  background: linear-gradient(180deg, #f8f9ff 0%, #eef1fb 100%);
  color: #58607a;
  border-color: #dbe2f8;
}

.keypad-btn--danger {
  background: linear-gradient(180deg, #ff7f8f 0%, #ee5f72 100%);
  color: #fff;
  border-color: #f47b8c;
  box-shadow: 0 3px 0 rgba(186, 61, 79, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.32);
}

.keypad-btn--danger:hover {
  background: linear-gradient(180deg, #ff8d9b 0%, #f36b7d 100%);
}

.dialog-calculator--business-dark {
  border-color: #2e3a58;
  background: linear-gradient(150deg, #1b2235 0%, #141a2a 52%, #1a2233 100%);
  box-shadow: 0 14px 30px rgba(6, 10, 20, 0.42);
}

.dialog-calculator--business-dark .dialog-calculator__theme-switch {
  background: rgba(255, 255, 255, 0.08);
}

.dialog-calculator--business-dark .dialog-theme-btn {
  color: #b7c6ee;
  border-radius: 0;
}

.dialog-calculator--business-dark .dialog-theme-btn:hover {
  color: #e5eeff;
  background: rgba(255, 255, 255, 0.14);
}

.dialog-calculator--business-dark .dialog-theme-btn--active {
  color: #1d2f63;
  background: #dce6ff;
  box-shadow: 0 2px 10px rgba(147, 173, 255, 0.2);
}

.dialog-calculator--business-dark .dialog-calculator__display {
  background: linear-gradient(160deg, #f0f2f9 0%, #e3e7f1 55%, #dce1ec 100%);
  color: #1e2638;
  border-color: #c7cfde;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78), inset 0 -8px 12px rgba(0, 0, 0, 0.06), 0 8px 18px rgba(4, 8, 18, 0.3);
}

.dialog-calculator--business-dark .dialog-calculator__display::after {
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.03) 0px,
    rgba(0, 0, 0, 0.03) 1px,
    rgba(0, 0, 0, 0) 3px,
    rgba(0, 0, 0, 0) 4px
  );
}

.dialog-calculator--business-dark .dialog-calculator__history {
  color: #4f5971;
}

.dialog-calculator--business-dark .dialog-calculator__expression {
  color: #2a3349;
}

.dialog-calculator--business-dark .dialog-calculator__result {
  color: #161d2d;
  text-shadow: none;
}

.dialog-calculator--business-dark .keypad-btn {
  background: linear-gradient(180deg, #2a3450 0%, #222c44 100%);
  border-color: #3c4a70;
  color: #ecf1ff;
  border-radius: 0;
  box-shadow: 0 3px 0 rgba(10, 14, 26, 0.44), inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

.dialog-calculator--business-dark .keypad-btn:hover {
  background: linear-gradient(180deg, #313d5e 0%, #26324d 100%);
}

.dialog-calculator--business-dark .keypad-btn--operator {
  background: linear-gradient(180deg, #314f97 0%, #284583 100%);
  border-color: #4462ab;
  color: #f2f6ff;
}

.dialog-calculator--business-dark .keypad-btn--equal {
  background: linear-gradient(180deg, #3f86ff 0%, #326ee8 100%);
  border-color: #4f90ff;
}

.dialog-calculator--business-dark .keypad-btn--soft {
  background: linear-gradient(180deg, #3a435b 0%, #2f3950 100%);
  border-color: #4a5471;
  color: #dce4f7;
}

.dialog-calculator--business-dark .keypad-btn--danger {
  background: linear-gradient(180deg, #d6677b 0%, #bb4f65 100%);
  border-color: #d6798b;
}

.dialog-calculator--ios-apple {
  border-color: rgba(255, 255, 255, 0.72);
  background: linear-gradient(155deg, rgba(255, 255, 255, 0.72) 0%, rgba(245, 248, 255, 0.66) 100%);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 18px 34px rgba(33, 55, 120, 0.16);
}

.dialog-calculator--ios-apple .dialog-calculator__theme-switch {
  background: rgba(118, 130, 164, 0.2);
}

.dialog-calculator--ios-apple .dialog-theme-btn {
  color: #5a627a;
}

.dialog-calculator--ios-apple .dialog-theme-btn:hover {
  color: #29314a;
  background: rgba(255, 255, 255, 0.72);
}

.dialog-calculator--ios-apple .dialog-theme-btn--active {
  color: #1f6fff;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 3px 10px rgba(69, 109, 197, 0.2);
}

.dialog-calculator--ios-apple .dialog-calculator__display {
  background: linear-gradient(160deg, #f7f9ff 0%, #eef3ff 58%, #e8efff 100%);
  color: #1d2942;
  border-color: rgba(151, 175, 237, 0.55);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95), inset 0 -8px 12px rgba(115, 138, 199, 0.12), 0 10px 20px rgba(74, 96, 148, 0.18);
}

.dialog-calculator--ios-apple .dialog-calculator__display::after {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.15) 44%,
    rgba(255, 255, 255, 0.02) 100%
  );
  opacity: 1;
}

.dialog-calculator--ios-apple .dialog-calculator__history {
  color: #66708a;
}

.dialog-calculator--ios-apple .dialog-calculator__expression {
  color: #33415f;
}

.dialog-calculator--ios-apple .dialog-calculator__result {
  color: #13213f;
  text-shadow: none;
}

.dialog-calculator--ios-apple .keypad-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94) 0%, rgba(241, 246, 255, 0.92) 100%);
  border-color: rgba(185, 203, 244, 0.95);
  color: #24314f;
  box-shadow: 0 4px 0 rgba(109, 132, 186, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.98);
}

.dialog-calculator--ios-apple .keypad-btn:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(236, 243, 255, 0.95) 100%);
  border-color: rgba(164, 188, 240, 0.98);
}

.dialog-calculator--ios-apple .keypad-btn--latched,
.dialog-calculator--ios-apple .keypad-btn:active {
  transform: translateY(2px) scale(0.99);
  box-shadow: inset 0 3px 8px rgba(122, 142, 194, 0.32), inset 0 -1px 0 rgba(255, 255, 255, 0.55);
}

.dialog-calculator--ios-apple .keypad-btn--operator {
  background: linear-gradient(180deg, #dbe8ff 0%, #c9dcff 100%);
  border-color: #a8c4ff;
  color: #1b4db8;
}

.dialog-calculator--ios-apple .keypad-btn--equal {
  background: linear-gradient(180deg, #4f8eff 0%, #2f73ff 100%);
  border-color: #4a86ff;
  color: #fff;
  box-shadow: 0 4px 0 rgba(44, 98, 209, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dialog-calculator--ios-apple .keypad-btn--equal:hover {
  background: linear-gradient(180deg, #5f99ff 0%, #3d7fff 100%);
  border-color: #5a92ff;
}

.dialog-calculator--ios-apple .keypad-btn--equal.keypad-btn--latched,
.dialog-calculator--ios-apple .keypad-btn--equal:active {
  background: linear-gradient(180deg, #3f7ef0 0%, #2b67db 100%);
  border-color: #3f7ce9;
  color: #fff;
  box-shadow: inset 0 3px 8px rgba(21, 57, 136, 0.42), inset 0 -1px 0 rgba(255, 255, 255, 0.3);
}

.dialog-calculator--ios-apple .keypad-btn--soft {
  background: linear-gradient(180deg, #f7f9ff 0%, #edf2ff 100%);
  border-color: #d0ddfb;
  color: #5c6784;
}

.dialog-calculator--ios-apple .keypad-btn--danger {
  background: linear-gradient(180deg, #ff7d8d 0%, #ff5f74 100%);
  border-color: #ff8e9c;
  color: #fff;
  box-shadow: 0 4px 0 rgba(205, 72, 92, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.dialog-calculator--ios-apple .keypad-btn--danger:hover {
  background: linear-gradient(180deg, #ff8c9a 0%, #ff6e81 100%);
  border-color: #ff9aa6;
}

.dialog-calculator--ios-apple .keypad-btn--danger.keypad-btn--latched,
.dialog-calculator--ios-apple .keypad-btn--danger:active {
  background: linear-gradient(180deg, #eb5f73 0%, #d84d64 100%);
  border-color: #e76f81;
  color: #fff;
  box-shadow: inset 0 3px 8px rgba(152, 42, 58, 0.36), inset 0 -1px 0 rgba(255, 255, 255, 0.26);
}

.dialog-calculator--macaron {
  border-color: #ffdbe8;
  background: linear-gradient(145deg, #fff7fb 0%, #fffaf2 45%, #f5fbff 100%);
  box-shadow: 0 16px 30px rgba(236, 170, 200, 0.22);
}

.dialog-calculator--macaron .dialog-calculator__theme-switch {
  background: rgba(255, 217, 233, 0.45);
}

.dialog-calculator--macaron .dialog-theme-btn {
  color: #9a6a86;
}

.dialog-calculator--macaron .dialog-theme-btn:hover {
  color: #7f4f6d;
  background: rgba(255, 255, 255, 0.82);
}

.dialog-calculator--macaron .dialog-theme-btn--active {
  color: #d3528f;
  background: #fff;
  box-shadow: 0 3px 10px rgba(227, 126, 175, 0.25);
}

.dialog-calculator--macaron .dialog-calculator__display {
  background: linear-gradient(155deg, #fff7f9 0%, #fff1f4 38%, #f7f6ff 100%);
  border-color: #f7cadf;
  color: #6a4b5d;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.98), inset 0 -8px 12px rgba(222, 170, 194, 0.18), 0 8px 16px rgba(224, 156, 190, 0.2);
}

.dialog-calculator--macaron .dialog-calculator__display::after {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.1) 45%,
    rgba(255, 255, 255, 0) 100%
  );
  opacity: 0.8;
}

.dialog-calculator--macaron .dialog-calculator__history {
  color: #9d7890;
}

.dialog-calculator--macaron .dialog-calculator__expression {
  color: #7c5a70;
}

.dialog-calculator--macaron .dialog-calculator__result {
  color: #5f4254;
  text-shadow: none;
}

.dialog-calculator--macaron .keypad-btn {
  background: linear-gradient(180deg, #ffe7f0 0%, #ffdce9 100%);
  border-color: #ffc7dc;
  color: #8b4f72;
  box-shadow: 0 3px 0 rgba(223, 149, 183, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.88);
}

.dialog-calculator--macaron .keypad-btn:hover {
  background: linear-gradient(180deg, #ffeef5 0%, #ffe1ed 100%);
  border-color: #ffbfd8;
}

.dialog-calculator--macaron .keypad-btn--latched,
.dialog-calculator--macaron .keypad-btn:active {
  box-shadow: inset 0 3px 8px rgba(206, 124, 163, 0.3), inset 0 -1px 0 rgba(255, 255, 255, 0.45);
}

.dialog-calculator--macaron .keypad-btn--operator {
  background: linear-gradient(180deg, #e8e0ff 0%, #daceff 100%);
  border-color: #cdb8ff;
  color: #6551a7;
}

.dialog-calculator--macaron .keypad-btn--equal {
  background: linear-gradient(180deg, #a8ead8 0%, #84dcc4 100%);
  border-color: #7ad8bd;
  color: #2b5f51;
  box-shadow: 0 3px 0 rgba(101, 189, 164, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.58);
}

.dialog-calculator--macaron .keypad-btn--equal:hover {
  background: linear-gradient(180deg, #b5efdf 0%, #93e4cd 100%);
}

.dialog-calculator--macaron .keypad-btn--equal.keypad-btn--latched,
.dialog-calculator--macaron .keypad-btn--equal:active {
  background: linear-gradient(180deg, #8adac4 0%, #73ccb4 100%);
  border-color: #68c2aa;
}

.dialog-calculator--macaron .keypad-btn--soft {
  background: linear-gradient(180deg, #fff1cc 0%, #ffe7b3 100%);
  border-color: #ffde9f;
  color: #8f6d2f;
}

.dialog-calculator--macaron .keypad-btn--danger {
  background: linear-gradient(180deg, #ffb1be 0%, #ff98aa 100%);
  border-color: #ff8fa3;
  color: #7f3143;
  box-shadow: 0 3px 0 rgba(218, 120, 142, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.58);
}

.dialog-calculator--macaron .keypad-btn--danger:hover {
  background: linear-gradient(180deg, #ffc0ca 0%, #ffa7b7 100%);
}

.dialog-calculator--macaron .keypad-btn--danger.keypad-btn--latched,
.dialog-calculator--macaron .keypad-btn--danger:active {
  background: linear-gradient(180deg, #ff98aa 0%, #f3879a 100%);
  border-color: #ef7f93;
}

.dialog-calculator--cyberpunk {
  border-color: #2b1f57;
  background: radial-gradient(circle at 12% 8%, rgba(37, 199, 255, 0.16) 0%, rgba(37, 199, 255, 0) 42%), radial-gradient(circle at 85% 14%, rgba(255, 57, 182, 0.18) 0%, rgba(255, 57, 182, 0) 48%), linear-gradient(145deg, #120a24 0%, #1a1131 46%, #110a23 100%);
  box-shadow: 0 18px 34px rgba(8, 4, 24, 0.62);
}

.dialog-calculator--cyberpunk .dialog-calculator__theme-switch {
  background: rgba(255, 255, 255, 0.08);
}

.dialog-calculator--cyberpunk .dialog-theme-btn {
  color: #d3c4ff;
}

.dialog-calculator--cyberpunk .dialog-theme-btn:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.16);
}

.dialog-calculator--cyberpunk .dialog-theme-btn--active {
  color: #0f0a2a;
  background: linear-gradient(180deg, #39f3ff 0%, #24d6ff 100%);
  box-shadow: 0 0 10px rgba(44, 224, 255, 0.5);
}

.dialog-calculator--cyberpunk .dialog-calculator__display {
  background: linear-gradient(160deg, #0d1328 0%, #101b37 58%, #0f1730 100%);
  border-color: rgba(76, 213, 255, 0.5);
  color: #c7f5ff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), inset 0 -12px 16px rgba(0, 0, 0, 0.38), 0 10px 20px rgba(4, 9, 24, 0.5);
}

.dialog-calculator--cyberpunk .dialog-calculator__display::after {
  background: repeating-linear-gradient(
    to bottom,
    rgba(57, 243, 255, 0.08) 0px,
    rgba(57, 243, 255, 0.08) 1px,
    rgba(57, 243, 255, 0) 3px,
    rgba(57, 243, 255, 0) 4px
  );
  opacity: 0.75;
}

.dialog-calculator--cyberpunk .dialog-calculator__history {
  color: #78ccff;
}

.dialog-calculator--cyberpunk .dialog-calculator__expression {
  color: #aee7ff;
}

.dialog-calculator--cyberpunk .dialog-calculator__result {
  color: #e8fbff;
  text-shadow: 0 0 12px rgba(74, 226, 255, 0.55);
}

.dialog-calculator--cyberpunk .keypad-btn {
  background: linear-gradient(180deg, #20123f 0%, #1a1033 100%);
  border-color: #3d2f73;
  color: #e9dcff;
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.52), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.dialog-calculator--cyberpunk .keypad-btn:hover {
  background: linear-gradient(180deg, #28164e 0%, #1f123d 100%);
  border-color: #4c3d88;
}

.dialog-calculator--cyberpunk .keypad-btn--latched,
.dialog-calculator--cyberpunk .keypad-btn:active {
  box-shadow: inset 0 3px 10px rgba(0, 0, 0, 0.56), inset 0 -1px 0 rgba(255, 255, 255, 0.12);
}

.dialog-calculator--cyberpunk .keypad-btn--operator {
  background: linear-gradient(180deg, #3a1a70 0%, #2d1456 100%);
  border-color: #6f46b8;
  color: #f2ddff;
}

.dialog-calculator--cyberpunk .keypad-btn--equal {
  background: linear-gradient(180deg, #19e8ff 0%, #0eb8ff 100%);
  border-color: #45dcff;
  color: #0d1a3a;
  box-shadow: 0 3px 0 rgba(7, 111, 168, 0.44), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dialog-calculator--cyberpunk .keypad-btn--equal:hover {
  background: linear-gradient(180deg, #39eeff 0%, #1dc8ff 100%);
}

.dialog-calculator--cyberpunk .keypad-btn--equal.keypad-btn--latched,
.dialog-calculator--cyberpunk .keypad-btn--equal:active {
  background: linear-gradient(180deg, #11cde3 0%, #0aa0e2 100%);
  border-color: #39c9f5;
}

.dialog-calculator--cyberpunk .keypad-btn--soft {
  background: linear-gradient(180deg, #2a1a52 0%, #20143f 100%);
  border-color: #54408f;
  color: #d6c2ff;
}

.dialog-calculator--cyberpunk .keypad-btn--danger {
  background: linear-gradient(180deg, #ff4ca8 0%, #ff2f8f 100%);
  border-color: #ff63b2;
  color: #fff;
  box-shadow: 0 3px 0 rgba(171, 37, 108, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.dialog-calculator--cyberpunk .keypad-btn--danger:hover {
  background: linear-gradient(180deg, #ff66b5 0%, #ff479f 100%);
}

.dialog-calculator--cyberpunk .keypad-btn--danger.keypad-btn--latched,
.dialog-calculator--cyberpunk .keypad-btn--danger:active {
  background: linear-gradient(180deg, #ee2d8d 0%, #d81f78 100%);
  border-color: #f251a3;
}

</style>

<style>
.workbench-tool-dialog {
  margin-top: 12vh !important;
}

.layout-menu .el-menu-item {
  margin: 4px 8px;
  border-radius: 6px;
  transition: all 0.3s;
}

.layout-menu .el-menu-item:hover {
  background-color: rgba(64, 158, 255, 0.1) !important;
}

.layout-menu .el-menu-item.is-active {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%) !important;
  color: #fff !important;
}

.layout-menu .el-submenu .el-menu-item {
  min-width: auto;
}
</style>
